<template>
  <section class="page-stack">
    <article class="card page-hero-card">
      <header class="page-hero-head">
        <div class="page-hero-copy">
          <p class="eyebrow">Gear Insights</p>
          <h2>Best gear by specialization</h2>
          <p>Aggregated from top Mythic+ leaderboard characters and cached locally for fast return visits.</p>
        </div>
      </header>
    </article>

    <div class="card filter-toolbar gear-insights-toolbar">
      <div class="applied-filters">
        <span class="filter-pill">Season: <span>{{ analysis?.seasonLabel || 'Current' }}</span></span>
        <span class="filter-pill">Region: <span>{{ analysis?.region || filters.region }}</span></span>
        <span class="filter-pill">Players: <span>{{ analyzedCharactersCount }}</span></span>
        <span class="filter-pill">Cache: <span>{{ cacheStatusLabel }}</span></span>
      </div>
      <div class="page-actions">
        <button class="btn-icon-outline" type="button" aria-label="Refresh analysis" title="Refresh analysis" :disabled="loading" @click="refreshAnalysis">
          <i class="fa-solid fa-rotate" :class="{ 'is-spinning': loading }"></i>
        </button>
      </div>
    </div>

    <div v-if="error" class="card">
      <section>{{ error }}</section>
    </div>

    <section v-if="loading" class="gear-insights-progress card">
      <header class="section-head">
        <div>
          <p class="eyebrow">Analyzing</p>
          <h3>{{ loadingMessage }}</h3>
        </div>
        <strong>{{ progressLabel }}</strong>
      </header>
      <div class="gear-progress-track">
        <span :style="{ width: progressPercent }"></span>
      </div>
    </section>

    <section v-if="classOptions.length" class="gear-selector-stack">
      <article class="card gear-choice-panel">
        <header class="section-head">
          <div>
            <p class="eyebrow">Class</p>
            <h3>{{ selectedClassLabel }}</h3>
          </div>
        </header>
        <div class="gear-class-switch">
          <button
            v-for="classItem in classOptions"
            :key="classItem.key"
            type="button"
            :class="{ 'is-active': selectedClassKey === classItem.key }"
            :style="{ '--class-color': classItem.color }"
            @click="selectClass(classItem.key)"
          >
            <img v-if="classItem.icon" :src="classItem.icon" :alt="classItem.name" loading="lazy">
            <span>{{ classItem.name }}</span>
            <small>({{ classItem.count }})</small>
          </button>
        </div>
      </article>

      <article v-if="specOptions.length" class="card gear-choice-panel">
        <header class="section-head">
          <div>
            <p class="eyebrow">Specialization</p>
            <h3>{{ selectedSpecLabel }}</h3>
          </div>
        </header>
        <div class="gear-spec-switch view-switch">
          <button
            v-for="spec in specOptions"
            :key="spec.key"
            class="btn-sm-outline"
            type="button"
            :class="{ 'is-active': selectedSpecKey === spec.key }"
            @click="selectedSpecKey = spec.key"
          >
            {{ spec.name }} ({{ spec.count }})
          </button>
        </div>
      </article>
    </section>

    <article v-if="selectedSpecData" class="gear-insights-summary">
      <section class="card gear-sample-panel">
        <header class="section-head">
          <div>
            <p class="eyebrow">Best set</p>
            <h3>{{ selectedSpecData.specName }} {{ selectedSpecData.className }}</h3>
          </div>
          <span class="filter-pill">Sample: <span>{{ selectedSpecData.characterCount }}</span></span>
        </header>

        <section class="armory-frame gear-insights-armory">
          <div class="armory-column armory-left">
            <a
              v-for="slot in leftGearSlots"
              :key="slot"
              class="armory-item"
              :class="{ 'is-empty': !bestItem(slot) }"
              :href="wowheadItemUrl(bestItem(slot))"
              :data-wowhead="wowheadItemData(bestItem(slot))"
              target="_blank"
              rel="noreferrer"
              :title="slotLabel(slot)"
              @click.prevent="openSlot(slot)"
            >
              <img v-if="bestItem(slot)" :src="itemIconUrl(bestItem(slot))" :alt="bestItem(slot).name" loading="eager">
              <span>
                <strong>{{ itemName(slot) }}</strong>
                <small>{{ itemMeta(slot) }}</small>
              </span>
            </a>
          </div>

          <div class="armory-column armory-right">
            <a
              v-for="slot in rightGearSlots"
              :key="slot"
              class="armory-item is-right"
              :class="{ 'is-empty': !bestItem(slot) }"
              :href="wowheadItemUrl(bestItem(slot))"
              :data-wowhead="wowheadItemData(bestItem(slot))"
              target="_blank"
              rel="noreferrer"
              :title="slotLabel(slot)"
              @click.prevent="openSlot(slot)"
            >
              <span>
                <strong>{{ itemName(slot) }}</strong>
                <small>{{ itemMeta(slot) }}</small>
              </span>
              <img v-if="bestItem(slot)" :src="itemIconUrl(bestItem(slot))" :alt="bestItem(slot).name" loading="eager">
            </a>
          </div>

          <div class="armory-weapons">
            <a
              v-for="slot in weaponGearSlots"
              :key="slot"
              class="armory-item armory-weapon"
              :class="{ 'is-empty': !bestItem(slot) }"
              :href="wowheadItemUrl(bestItem(slot))"
              :data-wowhead="wowheadItemData(bestItem(slot))"
              target="_blank"
              rel="noreferrer"
              :title="slotLabel(slot)"
              @click.prevent="openSlot(slot)"
            >
              <img v-if="bestItem(slot)" :src="itemIconUrl(bestItem(slot))" :alt="bestItem(slot).name" loading="eager">
              <span>
                <strong>{{ itemName(slot) }}</strong>
                <small>{{ itemMeta(slot) }}</small>
              </span>
            </a>
          </div>
        </section>
      </section>

      <section class="card gear-sample-characters">
        <header class="section-head">
          <div>
            <p class="eyebrow">Sample characters</p>
            <h3>{{ selectedSpecLabel }} {{ selectedClassLabel }}</h3>
          </div>
        </header>

        <div class="gear-character-tools">
          <label class="quick-search-field" for="gear-character-search">
            <i class="fa-solid fa-magnifying-glass"></i>
            <input
              id="gear-character-search"
              v-model.trim="characterSearch"
              class="input"
              type="search"
              placeholder="Search by name, realm or region"
              autocomplete="off"
            >
          </label>
          <div class="gear-character-stats applied-filters">
            <span class="filter-pill">Shown: <span>{{ visibleSpecCharacters.length }}/{{ filteredSpecCharacters.length }}</span></span>
            <span class="filter-pill">Sample: <span>{{ selectedSpecCharacters.length }}</span></span>
          </div>
        </div>

        <div v-if="filteredSpecCharacters.length" class="gear-character-list">
          <article v-for="character in visibleSpecCharacters" :key="character.key" class="gear-character-row">
            <a class="gear-character-identity" :href="character.profileUrl" target="_blank" rel="noreferrer">
              <span class="gear-character-avatar">
                <img v-if="character.thumbnailUrl" :src="character.thumbnailUrl" :alt="character.name" loading="lazy">
                <i v-else class="fa-solid fa-user"></i>
              </span>
              <span class="gear-character-copy">
                <strong>{{ character.name }}</strong>
                <small>{{ character.regionLabel }} &middot; {{ character.realm }}</small>
              </span>
            </a>
            <span class="gear-character-origin">
              <img :src="factionIcon(character.faction)" :alt="character.factionLabel" loading="lazy">
              <span>
                <strong>{{ character.factionLabel }}</strong>
                <small>{{ character.raceLabel }}</small>
              </span>
            </span>
            <span>
              <strong>{{ character.itemLevelLabel }}</strong>
              <small>Item level</small>
            </span>
            <span>
              <strong>{{ character.scoreLabel }}</strong>
              <small>M+ score</small>
            </span>
            <button
              class="btn-sm-outline"
              type="button"
              :disabled="addingFavoriteKey === character.key"
              @click="addSampleCharacterToFavorites(character)"
            >
              <i class="fa-solid" :class="addingFavoriteKey === character.key ? 'fa-spinner is-spinning' : 'fa-star'"></i>
              <span>{{ addingFavoriteKey === character.key ? 'Adding' : 'Favorite' }}</span>
            </button>
          </article>
        </div>
        <div v-else class="gear-character-empty">
          No characters match this search.
        </div>

        <AppPagination
          v-if="characterTotalPages > 1"
          v-model:page="characterPage"
          :total-pages="characterTotalPages"
          aria-label="Sample characters pagination"
        />
      </section>
    </article>

    <AppEmptyState
      v-else-if="!loading"
      icon="fa-solid fa-shirt"
      title="No gear analysis yet"
      message="Refresh the page data to analyze leaderboard characters and build specialization gear picks."
      :actions="[]"
    />

    <div
      v-if="activeSlot"
      class="app-modal-backdrop gear-backdrop"
      :class="{ 'is-closing': closingSlot }"
      @click.self="closeSlot"
    >
      <section class="app-modal gear-top-modal" role="dialog" aria-modal="true" aria-labelledby="gear-top-title">
        <header class="modal-head">
          <div>
            <p class="eyebrow">Top 5 items</p>
            <h2 id="gear-top-title">{{ slotLabel(activeSlot) }}</h2>
          </div>
          <button class="btn-icon-outline" type="button" aria-label="Close item list" @click="closeSlot">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </header>
        <section class="gear-top-list">
          <a
            v-for="(item, index) in activeSlotItems"
            :key="`${activeSlot}-${item.itemId}`"
            class="gear-top-item"
            :href="wowheadItemUrl(item)"
            :data-wowhead="wowheadItemData(item)"
            target="_blank"
            rel="noreferrer"
          >
            <span class="gear-top-rank">#{{ index + 1 }}</span>
            <img v-if="itemIconUrl(item)" :src="itemIconUrl(item)" :alt="item.name" loading="eager">
            <span>
              <strong>{{ item.name }}</strong>
              <small>{{ item.count }} equipped · {{ item.share }}%</small>
            </span>
            <i class="fa-solid fa-arrow-up-right-from-square"></i>
          </a>
        </section>
      </section>
    </div>
  </section>
</template>

<script>
import AppEmptyState from '../components/AppEmptyState.vue'
import AppPagination from '../components/AppPagination.vue'
import { getCharacterProfile, getMythicPlusRuns, getMythicPlusStaticData } from '../services/raiderApi'
import deathKnightIcon from '../assets/images/classes/death knight.png'
import demonHunterIcon from '../assets/images/classes/demon hunter.png'
import druidIcon from '../assets/images/classes/druid.png'
import evokerIcon from '../assets/images/classes/evoker.png'
import hunterIcon from '../assets/images/classes/hunter.png'
import mageIcon from '../assets/images/classes/mage.png'
import monkIcon from '../assets/images/classes/monk.png'
import paladinIcon from '../assets/images/classes/paladin.png'
import priestIcon from '../assets/images/classes/priest.png'
import rogueIcon from '../assets/images/classes/rogue.png'
import shamanIcon from '../assets/images/classes/shaman.png'
import warlockIcon from '../assets/images/classes/warlock.png'
import warriorIcon from '../assets/images/classes/warrior.png'
import allianceBanner from '../assets/images/alliance-banner.svg'
import allianceHordeBanner from '../assets/images/alliance-horde-banner.svg'
import hordeBanner from '../assets/images/horde-banner.svg'
import { ensureWowheadTooltips, refreshWowheadTooltips } from '../utils/wowheadTooltips'

const CACHE_KEY = 'rio-dashboard-gear-insights'
const CACHE_VERSION = 11
const CACHE_TTL = 24 * 60 * 60 * 1000
const ICON_BASE_URL = 'https://render.worldofwarcraft.com/us/icons/56'
const MIN_LEADERBOARD_PAGE_COUNT = 5
const MAX_LEADERBOARD_PAGE_COUNT = 40
const LEADERBOARD_PAGE_BATCH_SIZE = 5
const PROFILE_BATCH_SIZE = 5
const TOP_ITEM_LIMIT = 5
const MIN_CHARACTERS_PER_CLASS = 10

const ALL_CLASSES = [
  { key: 'death-knight', name: 'Death Knight' },
  { key: 'demon-hunter', name: 'Demon Hunter' },
  { key: 'druid', name: 'Druid' },
  { key: 'evoker', name: 'Evoker' },
  { key: 'hunter', name: 'Hunter' },
  { key: 'mage', name: 'Mage' },
  { key: 'monk', name: 'Monk' },
  { key: 'paladin', name: 'Paladin' },
  { key: 'priest', name: 'Priest' },
  { key: 'rogue', name: 'Rogue' },
  { key: 'shaman', name: 'Shaman' },
  { key: 'warlock', name: 'Warlock' },
  { key: 'warrior', name: 'Warrior' },
]

const CLASS_ICONS = {
  deathknight: deathKnightIcon,
  'death-knight': deathKnightIcon,
  'death knight': deathKnightIcon,
  demonhunter: demonHunterIcon,
  'demon-hunter': demonHunterIcon,
  'demon hunter': demonHunterIcon,
  druid: druidIcon,
  evoker: evokerIcon,
  hunter: hunterIcon,
  mage: mageIcon,
  monk: monkIcon,
  paladin: paladinIcon,
  priest: priestIcon,
  rogue: rogueIcon,
  shaman: shamanIcon,
  warlock: warlockIcon,
  warrior: warriorIcon,
}

const CLASS_COLORS = {
  deathknight: '#c41e3a',
  'death-knight': '#c41e3a',
  'death knight': '#c41e3a',
  demonhunter: '#a330c9',
  'demon-hunter': '#a330c9',
  'demon hunter': '#a330c9',
  druid: '#ff7c0a',
  evoker: '#33937f',
  hunter: '#aad372',
  mage: '#3fc7eb',
  monk: '#00ff98',
  paladin: '#f48cba',
  priest: '#ffffff',
  rogue: '#fff468',
  shaman: '#0070dd',
  warlock: '#8788ee',
  warrior: '#c69b6d',
}

const FACTION_ICONS = {
  alliance: allianceBanner,
  horde: hordeBanner,
  mixed: allianceHordeBanner,
}

const SLOT_LABELS = {
  head: 'Head',
  neck: 'Neck',
  shoulder: 'Shoulder',
  back: 'Back',
  chest: 'Chest',
  wrist: 'Wrist',
  hands: 'Hands',
  waist: 'Waist',
  legs: 'Legs',
  feet: 'Feet',
  finger1: 'Finger',
  finger2: 'Finger',
  finger: 'Finger',
  trinket1: 'Trinket',
  trinket2: 'Trinket',
  trinket: 'Trinket',
  mainhand: 'Main hand',
  offhand: 'Off hand',
}

function normalizeKey(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[_\s]+/g, '-')
}

function normalizeClassKey(value) {
  const key = normalizeKey(value)
  const aliases = {
    deathknight: 'death-knight',
    demonhunter: 'demon-hunter',
  }
  return aliases[key] || key
}

function normalizeSlotKey(slot) {
  return String(slot || '')
    .toLowerCase()
    .replace(/_/g, '')
    .replace(/-/g, '')
    .replace(/\s/g, '')
}

function loadCachedAnalysis() {
  try {
    const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null')
    return cached?.cacheVersion === CACHE_VERSION ? cached : null
  } catch (error) {
    return null
  }
}

function hasUsableCachedAnalysis(payload) {
  return Boolean(
    payload?.analysis
    && Date.now() - Number(payload.createdAt || 0) < CACHE_TTL
  )
}

function saveCachedAnalysis(payload) {
  localStorage.setItem(CACHE_KEY, JSON.stringify(payload))
}

function clearCachedAnalysis() {
  localStorage.removeItem(CACHE_KEY)
}

const analysisJobState = {
  loading: false,
  loadingMessage: 'Preparing leaderboard data',
  progressCurrent: 0,
  progressTotal: 0,
  error: '',
  result: null,
  promise: null,
}

const analysisJobListeners = new Set()

function analysisJobSnapshot() {
  return { ...analysisJobState }
}

function notifyAnalysisJob() {
  const snapshot = analysisJobSnapshot()
  analysisJobListeners.forEach((listener) => listener(snapshot))
}

function setAnalysisJobState(patch) {
  Object.assign(analysisJobState, patch)
  notifyAnalysisJob()
}

function subscribeAnalysisJob(listener) {
  analysisJobListeners.add(listener)
  listener(analysisJobSnapshot())
  return () => analysisJobListeners.delete(listener)
}

export default {
  name: 'GearInsightsView',
  components: { AppEmptyState, AppPagination },
  data() {
    return {
      loading: false,
      loadingMessage: 'Preparing leaderboard data',
      progressCurrent: 0,
      progressTotal: 0,
      error: '',
      analysis: null,
      selectedClassKey: '',
      selectedSpecKey: '',
      activeSlot: null,
      closingSlot: false,
      characterSearch: '',
      characterPage: 1,
      characterPageSize: 25,
      addingFavoriteKey: '',
      unsubscribeAnalysisJob: null,
      filters: {
        expansionId: 11,
        region: 'world',
        dungeon: 'all',
        season: '',
      },
      leftGearSlots: ['head', 'neck', 'shoulder', 'back', 'chest', 'shirt', 'tabard', 'wrist'],
      rightGearSlots: ['hands', 'waist', 'legs', 'feet', 'finger1', 'finger2', 'trinket1', 'trinket2'],
      weaponGearSlots: ['mainhand', 'offhand'],
    }
  },
  computed: {
    cacheStatusLabel() {
      if (!this.analysis?.createdAt) return 'Empty'
      const age = Date.now() - Number(this.analysis.createdAt)
      if (age < 60 * 1000) return 'Just now'
      if (age < 60 * 60 * 1000) return `${Math.round(age / 60000)} min ago`
      return `${Math.round(age / 3600000)} h ago`
    },
    analyzedCharactersCount() {
      return this.analysis?.characterCount || 0
    },
    classOptions() {
      const classes = this.analysis?.classes || {}
      const knownClassOptions = ALL_CLASSES.map((classItem) => {
        const item = classes[classItem.key] || {}
        return {
          key: classItem.key,
          name: item.name || classItem.name,
          color: CLASS_COLORS[classItem.key] || 'var(--primary)',
          icon: CLASS_ICONS[classItem.key],
          count: item.characterCount || 0,
        }
      })
      const customClassOptions = Object.entries(classes)
        .filter(([key]) => !ALL_CLASSES.some((classItem) => classItem.key === key))
        .map(([key, item]) => ({
          key,
          name: item.name || key,
          color: CLASS_COLORS[key] || 'var(--primary)',
          icon: CLASS_ICONS[key],
          count: item.characterCount || 0,
        }))
        .sort((left, right) => left.name.localeCompare(right.name))

      return [...knownClassOptions, ...customClassOptions]
    },
    specOptions() {
      const specs = this.analysis?.classes?.[this.selectedClassKey]?.specs || {}
      return Object.entries(specs)
        .map(([key, item]) => ({
          key,
          name: item.name || item.specName || 'Unknown',
          count: item.characterCount || 0,
        }))
        .sort((left, right) => left.name.localeCompare(right.name))
    },
    selectedClassLabel() {
      return this.classOptions.find((item) => item.key === this.selectedClassKey)?.name || 'Choose class'
    },
    selectedSpecLabel() {
      return this.specOptions.find((item) => item.key === this.selectedSpecKey)?.name || 'Choose spec'
    },
    selectedSpecData() {
      return this.analysis?.classes?.[this.selectedClassKey]?.specs?.[this.selectedSpecKey] || null
    },
    selectedSpecCharacters() {
      return this.selectedSpecData?.characters || []
    },
    filteredSpecCharacters() {
      const search = this.characterSearch.toLowerCase()
      if (!search) return this.selectedSpecCharacters

      return this.selectedSpecCharacters.filter((character) => {
        return [
          character.name,
          character.realm,
          character.region,
          character.regionLabel,
        ].some((value) => String(value || '').toLowerCase().includes(search))
      })
    },
    characterTotalPages() {
      return Math.max(1, Math.ceil(this.filteredSpecCharacters.length / this.characterPageSize))
    },
    visibleSpecCharacters() {
      const page = Math.min(this.characterPage, this.characterTotalPages)
      const start = (page - 1) * this.characterPageSize
      return this.filteredSpecCharacters.slice(start, start + this.characterPageSize)
    },
    displaySlotOrder() {
      return [...this.leftGearSlots, ...this.rightGearSlots, ...this.weaponGearSlots]
    },
    selectedBestItems() {
      if (!this.selectedSpecData?.slots) return {}

      return this.displaySlotOrder.reduce((items, slot) => {
        items[slot] = this.itemForDisplaySlot(slot)
        return items
      }, {})
    },
    activeSlotItems() {
      return this.itemsForSlot(this.activeSlot).slice(0, TOP_ITEM_LIMIT)
    },
    progressLabel() {
      if (!this.progressTotal) return ''
      return `${this.progressCurrent}/${this.progressTotal}`
    },
    progressPercent() {
      if (!this.progressTotal) return '8%'
      return `${Math.max(8, Math.round((this.progressCurrent / this.progressTotal) * 100))}%`
    },
  },
  mounted() {
    this.unsubscribeAnalysisJob = subscribeAnalysisJob(this.syncAnalysisJob)
    this.ensureWowheadTooltips()
    this.loadAnalysis()
    window.addEventListener('keydown', this.closeSlotOnEscape)
  },
  watch: {
    selectedClassKey() {
      this.resetCharacterList()
    },
    selectedSpecKey() {
      this.resetCharacterList()
    },
    characterSearch() {
      this.characterPage = 1
    },
    characterTotalPages(totalPages) {
      if (this.characterPage > totalPages) {
        this.characterPage = totalPages
      }
    },
  },
  beforeUnmount() {
    if (this.unsubscribeAnalysisJob) {
      this.unsubscribeAnalysisJob()
      this.unsubscribeAnalysisJob = null
    }
    window.removeEventListener('keydown', this.closeSlotOnEscape)
    document.body.classList.remove('is-scroll-locked')
  },
  methods: {
    syncAnalysisJob(snapshot) {
      this.loading = snapshot.loading
      this.loadingMessage = snapshot.loadingMessage
      this.progressCurrent = snapshot.progressCurrent
      this.progressTotal = snapshot.progressTotal
      this.error = snapshot.error

      if (snapshot.result && snapshot.result.createdAt !== this.analysis?.createdAt) {
        this.applyAnalysis(snapshot.result)
      }
    },
    async loadAnalysis() {
      if (analysisJobState.loading) {
        return
      }

      const cached = loadCachedAnalysis()
      if (hasUsableCachedAnalysis(cached)) {
        this.applyAnalysis(cached)
        return
      }

      await this.buildAnalysis()
    },
    async refreshAnalysis() {
      clearCachedAnalysis()
      if (!analysisJobState.loading) {
        analysisJobState.promise = null
      }
      setAnalysisJobState({
        result: null,
        error: '',
      })
      this.analysis = null
      await this.buildAnalysis(true)
    },
    applyAnalysis(payload) {
      this.analysis = payload
      this.selectedClassKey = this.classOptions[0]?.key || ''
      this.selectedSpecKey = this.specOptions[0]?.key || ''
      this.resetCharacterList()
      this.$nextTick(() => this.refreshWowheadTooltips())
    },
    selectClass(classKey) {
      this.selectedClassKey = classKey
      this.selectedSpecKey = this.specOptions[0]?.key || ''
    },
    async buildAnalysis(force = false) {
      if (analysisJobState.promise) {
        return analysisJobState.promise
      }

      setAnalysisJobState({
        loading: true,
        loadingMessage: 'Loading Mythic+ leaderboard',
        progressCurrent: 0,
        progressTotal: 0,
        error: '',
        result: null,
      })
      const loadingId = await this.$store.dispatch('startGlobalLoading', { id: 'page:gear-insights', message: 'Analyzing leaderboard gear...' })

      const promise = (async () => {
        const staticData = await getMythicPlusStaticData(this.filters.expansionId)
        const season = this.pickDefaultSeason(staticData.seasons || [])
        const seasonLabel = season?.short_name || season?.name || 'Current'
        const runFilters = {
          season: season?.slug || '',
          region: this.filters.region,
          dungeon: this.filters.dungeon,
        }
        const { characters, profiles } = await this.loadProfilesForMinimumClassCounts(runFilters, this.leaderboardSources(staticData, season))

        setAnalysisJobState({
          loadingMessage: 'Aggregating best items',
        })
        const payload = {
          cacheVersion: CACHE_VERSION,
          createdAt: Date.now(),
          season: season?.slug || '',
          seasonLabel,
          region: this.filters.region,
          dungeon: this.filters.dungeon,
          characterCount: profiles.length,
          leaderboardCharacterCount: characters.length,
          analysis: true,
          sampleComplete: this.hasMinimumProfilesPerClass(profiles),
          classes: this.aggregateProfiles(profiles),
        }

        saveCachedAnalysis(payload)
        setAnalysisJobState({
          result: payload,
        })
        this.$store.dispatch('setSeasonLabel', seasonLabel)
        this.$store.dispatch('notify', {
          type: 'success',
          title: force ? 'Gear refreshed' : 'Gear analysis ready',
          description: `Analyzed ${profiles.length} leaderboard characters.`,
        })
        return payload
      })()

      analysisJobState.promise = promise

      try {
        return await promise
      } catch (error) {
        const message = 'Unable to build gear analysis. Try refreshing again later.'
        setAnalysisJobState({
          error: message,
        })
        this.$store.dispatch('notify', {
          type: 'danger',
          title: 'Gear analysis failed',
          description: message,
        })
      } finally {
        setAnalysisJobState({
          loading: false,
          promise: null,
        })
        this.$store.dispatch('finishGlobalLoading', loadingId)
      }
    },
    pickDefaultSeason(seasons) {
      const seasonOptions = seasons
        .filter((season) => season.is_main_season)
        .sort((left, right) => new Date(right.starts?.us || 0) - new Date(left.starts?.us || 0))
      const now = Date.now()
      return seasonOptions.find((season) => {
        const start = new Date(season.starts?.us || season.starts?.eu || 0).getTime()
        const end = new Date(season.ends?.us || season.ends?.eu || 0).getTime()
        return start <= now && now <= end
      }) || seasonOptions[0] || null
    },
    uniqueCharacters(rankings) {
      const map = new Map()
      rankings.forEach((entry) => {
        ;(entry.run?.roster || []).forEach((member) => {
          const character = member.character || {}
          const key = this.characterIdentityKey(character)
          if (!character.name || map.has(key)) return
          map.set(key, {
            ...character,
            fallbackClassName: character.class?.name || character.class?.slug || '',
            fallbackSpecName: character.spec?.name || character.spec?.slug || '',
          })
        })
      })
      return Array.from(map.values())
    },
    characterIdentityKey(character) {
      return String(character.path || `${character.region?.slug}-${character.realm?.slug}-${character.name}`).toLowerCase()
    },
    leaderboardSources(staticData, season) {
      const regions = ['world', 'eu', 'us', 'kr', 'tw']
      const dungeons = (season?.dungeons?.length ? season.dungeons : staticData.dungeons || [])
        .map((dungeon) => dungeon.slug)
        .filter(Boolean)
      const sources = []
      const addSource = (region, dungeon, label) => {
        const key = `${region}:${dungeon}`
        if (sources.some((source) => source.key === key)) return
        sources.push({ key, region, dungeon, label })
      }

      addSource(this.filters.region, this.filters.dungeon, 'selected leaderboard')
      regions.forEach((region) => addSource(region, 'all', `${region.toUpperCase()} all dungeons`))
      dungeons.forEach((dungeon) => addSource('world', dungeon, `World ${dungeon}`))
      regions
        .filter((region) => region !== 'world')
        .forEach((region) => {
          dungeons.forEach((dungeon) => addSource(region, dungeon, `${region.toUpperCase()} ${dungeon}`))
        })

      return sources
    },
    async loadProfilesForMinimumClassCounts(runFilters, sources) {
      const rankings = []
      const profiles = []
      const fetchedCharacterKeys = new Set()
      let characters = []

      for (const source of sources) {
        for (let page = 0; page < MAX_LEADERBOARD_PAGE_COUNT; page += LEADERBOARD_PAGE_BATCH_SIZE) {
          const pages = Array.from(
            { length: Math.min(LEADERBOARD_PAGE_BATCH_SIZE, MAX_LEADERBOARD_PAGE_COUNT - page) },
            (_, index) => page + index
          )

          setAnalysisJobState({
            loadingMessage: `Loading ${source.label} pages ${pages[0] + 1}-${pages[pages.length - 1] + 1}`,
          })

          const sourceFilters = {
            ...runFilters,
            region: source.region,
            dungeon: source.dungeon,
          }
          const runPages = await Promise.all(
            pages.map((pageNumber) => getMythicPlusRuns({ ...sourceFilters, page: pageNumber }).catch(() => ({ rankings: [] })))
          )
          const pageRankings = runPages.flatMap((pageData) => pageData.rankings || [])
          rankings.push(...pageRankings)
          characters = this.uniqueCharacters(rankings)

          const newCharacters = characters.filter((character) => {
            const key = this.characterIdentityKey(character)
            return key && !fetchedCharacterKeys.has(key)
          })

          setAnalysisJobState({
            loadingMessage: 'Fetching character gear',
            progressTotal: fetchedCharacterKeys.size + newCharacters.length,
          })

          for (let index = 0; index < newCharacters.length; index += PROFILE_BATCH_SIZE) {
            const batch = newCharacters.slice(index, index + PROFILE_BATCH_SIZE)
            batch.forEach((character) => fetchedCharacterKeys.add(this.characterIdentityKey(character)))
            const results = await Promise.allSettled(batch.map((character) => this.fetchProfile(character)))
            profiles.push(...results.filter((result) => result.status === 'fulfilled').map((result) => result.value))
            setAnalysisJobState({
              progressCurrent: Math.min(fetchedCharacterKeys.size, characters.length),
            })
          }

          const classCounts = this.profileClassCounts(profiles)
          const minimumClassCount = Math.min(...ALL_CLASSES.map((classItem) => classCounts[classItem.key] || 0))
          setAnalysisJobState({
            loadingMessage: `Scanning classes: ${minimumClassCount}/${MIN_CHARACTERS_PER_CLASS} minimum per class`,
          })

          if (page + pages.length >= MIN_LEADERBOARD_PAGE_COUNT && this.hasMinimumProfilesPerClass(profiles)) {
            return { characters, profiles }
          }

          if (!pageRankings.length) {
            break
          }
        }
      }

      return { characters, profiles }
    },
    hasMinimumProfilesPerClass(profiles) {
      const classCounts = this.profileClassCounts(profiles)
      return ALL_CLASSES.every((classItem) => (classCounts[classItem.key] || 0) >= MIN_CHARACTERS_PER_CLASS)
    },
    profileClassCounts(profiles) {
      return profiles.reduce((counts, profile) => {
        const className = profile.class || profile.class_name || profile.fallbackClassName || 'Unknown'
        const specName = profile.active_spec_name || profile.spec || profile.fallbackSpecName || 'Unknown'
        if (className === 'Unknown' || specName === 'Unknown') return counts
        const classKey = normalizeClassKey(className)
        counts[classKey] = (counts[classKey] || 0) + 1
        return counts
      }, {})
    },
    async fetchProfile(character) {
      const profile = await getCharacterProfile({
        region: character.region?.slug,
        realm: character.realm?.slug,
        name: character.name,
        fields: 'gear,mythic_plus_scores_by_season:current,mythic_plus_ranks',
      })
      return {
        ...profile,
        fallbackClassName: character.fallbackClassName,
        fallbackSpecName: character.fallbackSpecName,
        fallbackRegionSlug: character.region?.slug,
        fallbackRealmSlug: character.realm?.slug,
      }
    },
    aggregateProfiles(profiles) {
      const classes = ALL_CLASSES.reduce((map, classItem) => {
        map[classItem.key] = {
          name: classItem.name,
          characterCount: 0,
          specs: {},
        }
        return map
      }, {})

      profiles.forEach((profile) => {
        const className = profile.class || profile.class_name || profile.fallbackClassName || 'Unknown'
        const specName = profile.active_spec_name || profile.spec || profile.fallbackSpecName || 'Unknown'
        if (className === 'Unknown' || specName === 'Unknown') return
        const classKey = normalizeClassKey(className)
        const specKey = normalizeKey(specName)

        if (!classes[classKey]) {
          classes[classKey] = {
            name: className,
            characterCount: 0,
            specs: {},
          }
        }

        if (!classes[classKey].specs[specKey]) {
          classes[classKey].specs[specKey] = {
            className,
            name: specName,
            specName,
            characterCount: 0,
            characters: [],
            characterKeys: new Set(),
            slotCounts: {},
          }
        }

        classes[classKey].characterCount += 1
        classes[classKey].specs[specKey].characterCount += 1
        const sampleCharacter = this.sampleCharacterFromProfile(profile, className, specName)
        if (sampleCharacter && !classes[classKey].specs[specKey].characterKeys.has(sampleCharacter.key)) {
          classes[classKey].specs[specKey].characterKeys.add(sampleCharacter.key)
          classes[classKey].specs[specKey].characters.push(sampleCharacter)
        }

        this.normalizedGearItems(profile).forEach((item) => {
          const slotKey = this.aggregateSlotKey(item.slotKey)
          const itemId = item.item_id || item.id
          if (!slotKey || !itemId || !SLOT_LABELS[slotKey]) return

          const slotCounts = classes[classKey].specs[specKey].slotCounts
          if (!slotCounts[slotKey]) slotCounts[slotKey] = {}
          if (!slotCounts[slotKey][itemId]) {
            slotCounts[slotKey][itemId] = {
              itemId,
              name: item.name || `Item ${itemId}`,
              icon: item.icon || '',
              itemLevel: item.item_level || item.itemLevel || null,
              bonuses: item.bonuses || [],
              gems: item.gems || [],
              enchant: item.enchant || null,
              enchant_id: item.enchant_id || null,
              count: 0,
            }
          }
          slotCounts[slotKey][itemId].count += 1
        })
      })

      Object.values(classes).forEach((classItem) => {
        Object.values(classItem.specs).forEach((spec) => {
          spec.slots = Object.entries(spec.slotCounts).reduce((slots, [slot, items]) => {
            slots[slot] = Object.values(items)
              .sort((left, right) => right.count - left.count || left.name.localeCompare(right.name))
              .slice(0, TOP_ITEM_LIMIT)
              .map((item) => ({
                ...item,
                share: Math.round((item.count / Math.max(spec.characterCount, 1)) * 100),
              }))
            return slots
          }, {})
          spec.characters.sort((left, right) => right.score - left.score || left.name.localeCompare(right.name))
          delete spec.slotCounts
          delete spec.characterKeys
        })
      })

      return classes
    },
    sampleCharacterFromProfile(profile, className, specName) {
      const region = String(profile.region || profile.fallbackRegionSlug || '').toLowerCase()
      const realm = profile.realm || profile.realm_slug || profile.fallbackRealmSlug || ''
      const name = profile.name || ''
      if (!region || !realm || !name) return null

      const score = Math.round(profile.mythic_plus_scores_by_season?.[0]?.scores?.all || 0)
      const itemLevel = Math.round(profile.gear?.item_level_equipped || profile.gear?.item_level || 0)
      const realmSlug = String(profile.realm_slug || profile.fallbackRealmSlug || realm).toLowerCase().replace(/\s+/g, '-')
      const faction = profile.faction || ''
      const race = profile.race || ''

      return {
        key: `${region}:${realmSlug}:${String(name).toLowerCase()}`,
        name,
        realm,
        realmSlug,
        region,
        regionLabel: region.toUpperCase(),
        className,
        specName,
        thumbnailUrl: profile.thumbnail_url || '',
        faction,
        factionLabel: faction || '-',
        race,
        raceLabel: race || '-',
        score,
        scoreLabel: score ? String(score) : '-',
        itemLevel,
        itemLevelLabel: itemLevel ? String(itemLevel) : '-',
        profileUrl: profile.profile_url || `https://raider.io/characters/${region}/${realmSlug}/${encodeURIComponent(name)}`,
      }
    },
    normalizedGearItems(profile) {
      const items = profile.gear?.items
      if (!items) return []

      if (Array.isArray(items)) {
        return items.map((item, index) => this.normalizeGearItem(item, item.slot || item.slot_key || `${index}`))
      }

      return Object.entries(items).map(([slotKey, item]) => this.normalizeGearItem(item, slotKey))
    },
    normalizeGearItem(item, slotKey) {
      return {
        ...(item || {}),
        slotKey: normalizeSlotKey(slotKey || item?.slot || item?.inventory_type?.type),
      }
    },
    bestItem(slot) {
      return this.selectedBestItems[slot] || null
    },
    itemForDisplaySlot(slot) {
      const items = this.itemsForSlot(slot)
      if (slot === 'finger2' || slot === 'trinket2') {
        return items[1] || null
      }
      return items[0] || null
    },
    itemsForSlot(slot) {
      return this.selectedSpecData?.slots?.[this.aggregateSlotKey(slot)] || []
    },
    aggregateSlotKey(slot) {
      const normalizedSlot = normalizeSlotKey(slot)
      if (normalizedSlot === 'finger1' || normalizedSlot === 'finger2') return 'finger'
      if (normalizedSlot === 'trinket1' || normalizedSlot === 'trinket2') return 'trinket'
      return normalizedSlot
    },
    itemName(slot) {
      return this.bestItem(slot)?.name || this.slotLabel(slot)
    },
    itemMeta(slot) {
      const item = this.bestItem(slot)
      if (!item && (slot === 'shirt' || slot === 'tabard')) return '-'
      return item ? `${item.count} equipped · ${item.share}%` : 'No data'
    },
    slotLabel(slot) {
      return SLOT_LABELS[slot] || 'Empty slot'
    },
    itemIconUrl(item) {
      if (!item?.icon) return ''
      const icon = String(item.icon)
      if (icon.startsWith('http')) return icon
      return `${ICON_BASE_URL}/${icon}.jpg`
    },
    wowheadItemUrl(item) {
      const itemId = item?.itemId || item?.item_id || item?.id
      return itemId ? `https://www.wowhead.com/item=${itemId}` : 'https://www.wowhead.com/'
    },
    wowheadItemData(item) {
      const itemId = item?.itemId || item?.item_id || item?.id
      if (!itemId) return null
      const params = [`item=${itemId}`]
      const itemLevel = item.itemLevel || item.item_level
      if (itemLevel) params.push(`ilvl=${itemLevel}`)
      if (Array.isArray(item.bonuses) && item.bonuses.length) params.push(`bonus=${item.bonuses.join(':')}`)
      if (Array.isArray(item.gems) && item.gems.length) params.push(`gems=${item.gems.map((gem) => gem.item_id || gem.id || gem).join(':')}`)
      if (item.enchant?.id || item.enchant_id) params.push(`ench=${item.enchant?.id || item.enchant_id}`)
      return params.join('&')
    },
    openSlot(slot) {
      if (!this.itemsForSlot(slot).length) return
      this.activeSlot = slot
      this.closingSlot = false
      document.body.classList.add('is-scroll-locked')
      this.$nextTick(() => this.refreshWowheadTooltips())
    },
    closeSlot() {
      if (!this.activeSlot || this.closingSlot) return
      this.closingSlot = true
      window.setTimeout(() => {
        this.activeSlot = null
        this.closingSlot = false
        document.body.classList.remove('is-scroll-locked')
      }, 190)
    },
    closeSlotOnEscape(event) {
      if (event.key === 'Escape' && this.activeSlot) {
        this.closeSlot()
      }
    },
    resetCharacterList() {
      this.characterSearch = ''
      this.characterPage = 1
    },
    factionKey(faction) {
      const key = String(faction || '').toLowerCase()
      return key === 'alliance' || key === 'horde' ? key : 'mixed'
    },
    factionIcon(faction) {
      return FACTION_ICONS[this.factionKey(faction)]
    },
    async addSampleCharacterToFavorites(character) {
      if (!character?.region || !character?.realmSlug || !character?.name || this.addingFavoriteKey) return

      this.addingFavoriteKey = character.key
      const loadingId = await this.$store.dispatch('startGlobalLoading', {
        id: `gear-insights:favorite:${character.key}`,
        message: `Loading ${character.name} profile...`,
      })

      try {
        const profile = await getCharacterProfile({
          region: character.region,
          realm: character.realmSlug,
          name: character.name,
          fields: [
            'gear',
            'talents',
            'guild',
            'raid_progression:current-expansion',
            'mythic_plus_scores_by_season:current',
            'mythic_plus_best_runs:all',
            'mythic_plus_recent_runs',
            'mythic_plus_ranks',
            'realm',
          ].join(','),
        })
        this.$store.dispatch('addFavorite', { character: profile, source: 'gear_insights' })
      } catch (error) {
        this.$store.dispatch('notify', {
          type: 'danger',
          title: 'Favorite failed',
          description: `Unable to add ${character.name} to favorites.`,
        })
      } finally {
        this.addingFavoriteKey = ''
        this.$store.dispatch('finishGlobalLoading', loadingId)
      }
    },
    ensureWowheadTooltips() {
      ensureWowheadTooltips()
    },
    refreshWowheadTooltips() {
      this.$nextTick(() => {
        refreshWowheadTooltips()
      })
    },
  },
}
</script>
